import { create } from 'zustand';
import {
  DEFAULT_VIEWPORT,
  ZOOM_LIMITS,
  zoomAt,
  panBy,
  type Viewport,
} from '../viewport';
import type { Vec2 } from '@/core/types';

export interface SceneEditorState {
  viewport: Viewport;
  setViewport: (viewport: Viewport | ((prev: Viewport) => Viewport)) => void;
  setZoom: (zoom: number) => void;
  setPan: (panX: number, panY: number) => void;
  zoomIn: (anchor?: Vec2) => void;
  zoomOut: (anchor?: Vec2) => void;
  zoomAtPoint: (factor: number, anchor: Vec2) => void;
  panByOffset: (dx: number, dy: number) => void;
  resetViewport: () => void;
}

export const useSceneEditorStore = create<SceneEditorState>()((set) => ({
  viewport: DEFAULT_VIEWPORT,

  setViewport: (viewport) =>
    set((state) => ({
      viewport: typeof viewport === 'function' ? viewport(state.viewport) : viewport,
    })),

  setZoom: (zoom) =>
    set((state) => ({
      viewport: {
        ...state.viewport,
        zoom: Math.max(ZOOM_LIMITS.min, Math.min(zoom, ZOOM_LIMITS.max)),
      },
    })),

  setPan: (panX, panY) =>
    set((state) => ({
      viewport: { ...state.viewport, panX, panY },
    })),

  zoomIn: (anchor = { x: 400, y: 300 }) =>
    set((state) => ({
      viewport: zoomAt(state.viewport, 1.2, anchor),
    })),

  zoomOut: (anchor = { x: 400, y: 300 }) =>
    set((state) => ({
      viewport: zoomAt(state.viewport, 1 / 1.2, anchor),
    })),

  zoomAtPoint: (factor, anchor) =>
    set((state) => ({
      viewport: zoomAt(state.viewport, factor, anchor),
    })),

  panByOffset: (dx, dy) =>
    set((state) => ({
      viewport: panBy(state.viewport, dx, dy),
    })),

  resetViewport: () => set({ viewport: DEFAULT_VIEWPORT }),
}));
